import type {
    DailyFunnelDto,
    DailyFunnelStepDto,
    DailyStageSlaDto,
    MetricDto,
    StageApplicability,
    StageKey,
} from "@/features/daily/types/daily.types";
import type { DailyQueryState } from "@/features/daily/hooks/use-daily-query-state";
import {
    DailyTone,
    commandViewModel,
    dailyFormat,
} from "@/features/daily/components/command/command-view-model";

type SlaStage = DailyStageSlaDto["stage"];
type PercentileMethod = NonNullable<DailyStageSlaDto["percentileMethod"]>;

/**
 * D34/D60: a t-digest percentile is an ESTIMATE, and the estimate says so on screen.
 * An exact rank needs no qualifier, so its entry is deliberately `null` rather than
 * a reassuring word — the map stays TOTAL either way.
 */
const PERCENTILE_METHOD_TEXT: Record<PercentileMethod, string | null> = {
    exact_nearest_rank: null,
    tdigest_approximate: "aprox.",
};

const STAGE_TEXT: Record<StageKey, string> = {
    funding_wait: "Espera de fondos",
    exchange: "Conversión de fondos",
    outflow: "Entrega al destino",
    total_funded: "Ejecución completa",
};

/**
 * Enmienda C5: the wait for the customer's money is NOT an SLA stage — nobody here
 * decides when they pay. It is reported, separately and explicitly, as information.
 */
const STAGE_SLA_DESCRIPTION: Record<SlaStage, string> = {
    exchange: "De fondos recibidos a convertidos.",
    outflow: "De convertidos al fin proyectado.",
    total_funded: "De fondos recibidos al fin proyectado.",
};

/**
 * Enmienda C6: absence of evidence that a stage ran is NOT evidence that the route
 * skips it. `unknown` stays in the denominator and says so.
 */
const APPLICABILITY_TEXT: Record<StageApplicability, string> = {
    applicable: "Aplica a esta población",
    not_applicable: "No aplica a esta ruta",
    unknown: "No se puede determinar",
};

export type MetricCellVm = {
    label: string;
    valueLabel: string;
    qualityLabel: string;
    tone: DailyTone;
    sampleLabel: string;
    reasons: string[];
};

export type FunnelStepVm = {
    key: string;
    stageLabel: string;
    applicability: StageApplicability;
    applicabilityLabel: string;
    entered: MetricCellVm;
    advanced: MetricCellVm;
    pending: MetricCellVm;
    failed: MetricCellVm;
    canceled: MetricCellVm;
    refunded: MetricCellVm;
    /** SC-020: `null` whenever the stage is not applicable to the observed route. */
    dropOffCount: number | null;
    dropOffLabel: string;
};

export type StageSlaVm = {
    stage: SlaStage;
    stageLabel: string;
    description: string;
    applicability: StageApplicability;
    applicabilityLabel: string;
    targetLabel: string;
    complianceLabel: string;
    complianceTone: DailyTone;
    qualityLabel: string;
    /** Enmienda C11/D35: what was measured, against what was eligible to be measured. */
    sampleLabel: string;
    p50Label: string;
    p95Label: string;
    durationsMeasured: boolean;
    durationsMissingLabel: string;
    /** `null` when the source did not say how the percentile was computed. */
    percentileLabel: string | null;
    /** FR-021/D61: how old what is STILL open in this stage is. */
    openAgeP95Label: string;
    breachedOpen: number;
    breachedLate: number;
    /** SC-021: the operations that actually breached — never the size of the sample. */
    affected: number;
    affectedLabel: string;
    reasons: string[];
    queueHref: string;
};

export type FunnelContextVm = {
    scopeLabel: string;
    windowLabel: string;
    timeZone: string;
    qualityLabel: string;
    tone: DailyTone;
    reasons: string[];
    policyLabel: string;
    fundingWaitLabel: string;
    cohortLabel: string;
    coverageLabel: string;
};

type DateBasis = DailyFunnelDto["envelope"]["window"]["dateBasis"];

// FR-003: which cohort this page is about is a fact on screen, not an assumption.
// A funnel read on "created" answers a different question from one read on "funded".
const COHORT_TEXT: Record<DateBasis, string> = {
    createdAt: "Operaciones creadas en la ventana (demanda)",
    receivedAt: "Operaciones financiadas en la ventana (ejecución)",
    completedAt: "Operaciones completadas en la ventana (rendimiento)",
    now: "Todo lo abierto ahora mismo (pendiente)",
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null && !Array.isArray(value);

const isNumberOrNull = (value: unknown): boolean => value === null || typeof value === "number";

/** D24: the cache hands back `unknown`; the caller proves the shape, never casts it. */
export const isFunnelDto = (value: unknown): value is DailyFunnelDto =>
    isRecord(value)
    && isRecord(value.envelope)
    && Array.isArray(value.conversionJourney)
    && Array.isArray(value.executionJourney)
    && Array.isArray(value.stages)
    && isNumberOrNull(value.fundingWaitP95Ms);

/**
 * Enmienda C11 / D35: `sample` is what was measured. "Eligible" is the population it
 * was measured out of — the denominator when the metric carries one, otherwise the
 * coverage the source expected. When neither exists the screen says so instead of
 * quietly reporting the sample twice.
 */
const eligibleOf = (metric: MetricDto): number | null =>
    metric.denominator ?? metric.coverage.expected;

/**
 * D61: an age the source did not report is reported as not reported. Rendering it as
 * a zero would say "nothing has been waiting", which is the opposite claim.
 */
const openAgeLabelOf = (ms: number | null | undefined): string =>
    ms === undefined || ms === null ? `${dailyFormat.absent} (sin medir)` : dailyFormat.duration(ms);

const sampleLabelOf = (metric: MetricDto): string => {
    const eligible = eligibleOf(metric);
    if (eligible === null) return `Muestra ${metric.sample} · población elegible sin informar`;
    return `Muestra ${metric.sample} de ${eligible} elegibles`;
};

const cell = (label: string, metric: MetricDto): MetricCellVm => ({
    label,
    valueLabel: dailyFormat.count(metric.value),
    qualityLabel: commandViewModel.qualityLabel(metric.quality),
    tone: commandViewModel.metricTone(metric.quality),
    sampleLabel: sampleLabelOf(metric),
    reasons: commandViewModel.reasonTexts(metric.reasonCodes),
});

/**
 * SC-020: a route with no exchange leg produces NO drop-off. Reporting one would
 * invent a loss where the journey simply does not pass through that stage — and the
 * same holds for `unknown`, where we have no positive evidence either way.
 */
const dropOffOf = (step: DailyFunnelStepDto): number | null => {
    if (step.applicability !== "applicable") return null;
    if (step.entered.value === null || step.advanced.value === null) return null;
    return step.entered.value - step.advanced.value;
};

const dropOffLabelOf = (step: DailyFunnelStepDto, dropOffCount: number | null): string => {
    if (dropOffCount === null) return dailyFormat.absent;
    const entered = step.entered.value;
    if (entered === null || entered === 0) return dailyFormat.count(dropOffCount);
    return `${dailyFormat.count(dropOffCount)} (${dailyFormat.ratioPercent(dropOffCount / entered)})`;
};

const stageQueueHref = (query: DailyQueryState, stage: SlaStage): string =>
    commandViewModel.diagnosticHref({
        queue: "overdue",
        stage,
        window: query.windowPreset ?? "today",
        from: query.windowFrom ?? "",
        to: query.windowTo ?? "",
        scope: query.scope.join(","),
        types: query.types.join(","),
    });

export type JourneyEndVm = {
    label: string;
    valueLabel: string;
    note: string;
};

export type JourneyCardVm = {
    from: JourneyEndVm;
    to: JourneyEndVm;
    /** 0–100 share that advanced; `null` when either side has no value (never a zero bar). */
    progressPct: number | null;
    footnote: string;
    pending: number | null;
    tone: DailyTone;
    reasons: string[];
};

export type ComplianceVm = {
    valueLabel: string;
    detail: string;
    tone: DailyTone;
};

const shareOf = (part: number | null, whole: number | null): number | null =>
    part === null || whole === null || whole === 0 ? null : (part / whole) * 100;

const journeyEnds = (steps: FunnelStepVm[]): { first: FunnelStepVm; last: FunnelStepVm } | null => {
    const first = steps[0];
    const last = steps[steps.length - 1];
    return first === undefined || last === undefined ? null : { first, last };
};

const metricValue = (metric: MetricDto): number | null => metric.value;

export const funnelViewModel = {
    /**
     * FR-020: the commercial question — did the payer fund what they created? A
     * payer who never paid is demand, not a platform failure, so nothing here is
     * dressed as an error.
     */
    conversionCard: (steps: DailyFunnelStepDto[]): JourneyCardVm | null => {
        const vms = funnelViewModel.journey(steps);
        const ends = journeyEnds(vms);
        const firstDto = steps[0];
        const lastDto = steps[steps.length - 1];
        if (ends === null || firstDto === undefined || lastDto === undefined) return null;
        const pct = shareOf(metricValue(lastDto.advanced), metricValue(firstDto.entered));
        return {
            from: { label: "Creadas", valueLabel: ends.first.entered.valueLabel, note: ends.first.entered.sampleLabel },
            to: {
                label: "Financiadas",
                valueLabel: ends.last.advanced.valueLabel,
                note: pct === null ? "Proporción sin medir" : `${dailyFormat.ratioPercent(pct / 100)} de las creadas`,
            },
            progressPct: pct,
            footnote: `${ends.first.dropOffLabel} sin financiar · ${ends.first.pending.valueLabel} esperando fondos · ${ends.first.canceled.valueLabel} canceladas · ${ends.first.failed.valueLabel} fallidas`,
            pending: metricValue(firstDto.pending),
            tone: ends.first.entered.tone,
            reasons: ends.first.entered.reasons,
        };
    },

    /** FR-020: the operational question — once the money arrived, did we finish? */
    executionCard: (steps: DailyFunnelStepDto[]): JourneyCardVm | null => {
        const vms = funnelViewModel.journey(steps);
        const ends = journeyEnds(vms);
        const firstDto = steps[0];
        const lastDto = steps[steps.length - 1];
        if (ends === null || firstDto === undefined || lastDto === undefined) return null;
        const pct = shareOf(metricValue(lastDto.advanced), metricValue(firstDto.entered));
        const pending = metricValue(lastDto.pending);
        return {
            from: { label: "Financiadas", valueLabel: ends.first.entered.valueLabel, note: "Conjunto de entrada" },
            to: {
                label: "Completadas",
                valueLabel: ends.last.advanced.valueLabel,
                note: pending === null ? "En curso: sin medir" : `${dailyFormat.count(pending)} todavía en curso`,
            },
            progressPct: pct,
            footnote: `${ends.last.failed.valueLabel} fallidas · ${ends.last.canceled.valueLabel} canceladas · ${ends.last.refunded.valueLabel} reembolsadas`,
            pending,
            tone: ends.first.entered.tone,
            reasons: ends.first.entered.reasons,
        };
    },

    /**
     * Enmienda C11: compliance is quoted WITH its denominator — the operations whose
     * target has already passed — or with the sample it came from when the source
     * sent no denominator.
     */
    compliance: (stage: DailyStageSlaDto | undefined): ComplianceVm | null => {
        if (stage === undefined) return null;
        const metric = stage.compliance;
        const detail = metric.numerator !== undefined && metric.denominator !== undefined
            ? `${dailyFormat.count(metric.numerator)} de ${dailyFormat.count(metric.denominator)} a tiempo, entre operaciones cuyo plazo ya venció.`
            : `${sampleLabelOf(metric)}.`;
        return {
            valueLabel: dailyFormat.ratioPercent(metric.value),
            detail,
            tone: commandViewModel.metricTone(metric.quality),
        };
    },

    context: (dto: DailyFunnelDto): FunnelContextVm => ({
        scopeLabel: dto.envelope.scope.label,
        windowLabel: commandViewModel.windowLabel(dto.envelope.window),
        timeZone: dto.envelope.window.timeZone,
        qualityLabel: commandViewModel.qualityLabel(dto.envelope.quality),
        tone: commandViewModel.metricTone(dto.envelope.quality),
        reasons: commandViewModel.reasonTexts(dto.envelope.reasonCodes),
        policyLabel: dailyFormat.policy(dto.envelope.policy.policyVersion, dto.envelope.policy.policyFingerprint),
        // C5: informative, and labelled as such wherever it appears.
        fundingWaitLabel: dto.fundingWaitP95Ms === null
            ? dailyFormat.absent
            : dailyFormat.duration(dto.fundingWaitP95Ms),
        cohortLabel: COHORT_TEXT[dto.envelope.window.dateBasis],
        coverageLabel: dto.envelope.coverage.expected === null
            ? `${dto.envelope.coverage.observed} observadas · población esperada sin informar`
            : `${dto.envelope.coverage.observed} observadas de ${dto.envelope.coverage.expected} esperadas`,
    }),

    journey: (steps: DailyFunnelStepDto[]): FunnelStepVm[] =>
        steps.map((step) => {
            const dropOffCount = dropOffOf(step);
            return {
                key: step.key,
                stageLabel: STAGE_TEXT[step.stage],
                applicability: step.applicability,
                applicabilityLabel: APPLICABILITY_TEXT[step.applicability],
                entered: cell("Entraron", step.entered),
                advanced: cell("Avanzaron", step.advanced),
                pending: cell("Pendientes", step.pending),
                failed: cell("Fallidas", step.failed),
                canceled: cell("Canceladas", step.canceled),
                refunded: cell("Reembolsadas", step.refunded),
                dropOffCount,
                dropOffLabel: dropOffLabelOf(step, dropOffCount),
            };
        }),

    stages: (stages: DailyStageSlaDto[], query: DailyQueryState): StageSlaVm[] =>
        stages.map((stage) => {
            const durationsMeasured = stage.p50Ms !== null || stage.p95Ms !== null;
            return {
                stage: stage.stage,
                stageLabel: STAGE_TEXT[stage.stage],
                description: STAGE_SLA_DESCRIPTION[stage.stage],
                applicability: stage.applicability,
                applicabilityLabel: APPLICABILITY_TEXT[stage.applicability],
                targetLabel: dailyFormat.duration(stage.targetMs),
                complianceLabel: dailyFormat.ratioPercent(stage.compliance.value),
                complianceTone: commandViewModel.metricTone(stage.compliance.quality),
                qualityLabel: commandViewModel.qualityLabel(stage.compliance.quality),
                sampleLabel: sampleLabelOf(stage.compliance),
                p50Label: dailyFormat.duration(stage.p50Ms),
                p95Label: dailyFormat.duration(stage.p95Ms),
                durationsMeasured,
                // FR-022: no synthetic series and no invented percentile. When the
                // evidence is too thin the screen says how thin, and draws nothing.
                durationsMissingLabel: stage.compliance.quality === "unsupported"
                    ? "La fuente no admite duraciones para esta población"
                    : `Muestra insuficiente (n = ${stage.compliance.sample})`,
                breachedOpen: stage.breachedOpen,
                breachedLate: stage.breachedLate,
                affected: stage.affected,
                affectedLabel: `${dailyFormat.counted(stage.affected, "operación superó", "operaciones superaron")} este plazo`,
                // Fail closed: a source that does not name its method gets no badge,
                // because "no badge" must keep meaning "exact unless stated".
                percentileLabel: stage.percentileMethod === undefined ? null : PERCENTILE_METHOD_TEXT[stage.percentileMethod],
                openAgeP95Label: openAgeLabelOf(stage.openAgeP95Ms),
                reasons: commandViewModel.reasonTexts(stage.compliance.reasonCodes),
                queueHref: stageQueueHref(query, stage.stage),
            };
        }),
};
